import { LocgEvent, ScrollTargetEvent } from '@app/code/src/machines/locg/locgMachine.types'
import { LoggedOutGeneratorSection } from '../../../LoggedOutCodeGenerator.types'
import { useCallback, useMemo } from 'react'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useSelector } from '@xstate/react'
import useTranslation from 'next-translate/useTranslation'

interface ProgressBarDesktopSection {
  id: LoggedOutGeneratorSection
  width: number
  onClick: () => void
  label?: string
  hoverLabel?: string
}

export const useProgressBarDesktopSections = (): ProgressBarDesktopSection[] => {
  const { locgService } = useGlobalServices()
  const scanDestinationSectionActive = useSelector(locgService, state =>
    state.matches('scanDestination')
  )
  const authSectionActive = useSelector(locgService, state => state.matches('auth'))
  const { t } = useTranslation('logged-out-generator')

  const handleScanDestinationClick = useCallback(() => {
    if (scanDestinationSectionActive) return
    const actions: LocgEvent[] = [{ type: 'BACK' }]
    if (authSectionActive) actions.unshift({ type: 'BACK' })
    locgService.send(actions)
  }, [authSectionActive, locgService, scanDestinationSectionActive])

  const handleDesignSectionClick = useCallback(
    (id: LoggedOutGeneratorSection) => {
      return () => {
        let eventType: ScrollTargetEvent['type']
        if (scanDestinationSectionActive) eventType = 'NEXT'
        else if (authSectionActive) eventType = 'BACK'
        else eventType = id

        locgService.send({
          type: eventType,
          target: id
        })
      }
    },
    [scanDestinationSectionActive, authSectionActive, locgService]
  )

  const handleAuthSectionClick = useCallback(() => {
    if (authSectionActive) return
    locgService.send({
      type: 'DOWNLOAD_CODE'
    })
  }, [authSectionActive, locgService])

  return useMemo(() => {
    return [
      {
        id: LoggedOutGeneratorSection.ScanDestination,
        width: 1,
        label: t('LoggedOutGenerator.ProgressBar.desktop.sections.scanDestination.label'),
        onClick: handleScanDestinationClick
      },
      {
        id: LoggedOutGeneratorSection.ColorSelector,
        width: 0.25,
        label: t('LoggedOutGenerator.ProgressBar.desktop.sections.codeDesign.label'),
        hoverLabel: t(
          'LoggedOutGenerator.ProgressBar.desktop.sections.codeDesign.sections.color.label'
        ),
        onClick: handleDesignSectionClick(LoggedOutGeneratorSection.ColorSelector)
      },
      {
        id: LoggedOutGeneratorSection.ShapeSelector,
        width: 0.25,
        hoverLabel: t(
          'LoggedOutGenerator.ProgressBar.desktop.sections.codeDesign.sections.shape.label'
        ),
        onClick: handleDesignSectionClick(LoggedOutGeneratorSection.ShapeSelector)
      },
      {
        id: LoggedOutGeneratorSection.FrameAndText,
        width: 0.25,
        hoverLabel: t(
          'LoggedOutGenerator.ProgressBar.desktop.sections.codeDesign.sections.frameAndText.label'
        ),
        onClick: handleDesignSectionClick(LoggedOutGeneratorSection.FrameAndText)
      },
      {
        id: LoggedOutGeneratorSection.Logo,
        width: 0.25,
        hoverLabel: t(
          'LoggedOutGenerator.ProgressBar.desktop.sections.codeDesign.sections.logo.label'
        ),
        onClick: handleDesignSectionClick(LoggedOutGeneratorSection.Logo)
      },
      {
        id: LoggedOutGeneratorSection.SignUp,
        width: 1,
        label: t('LoggedOutGenerator.ProgressBar.desktop.sections.signUp.label'),
        onClick: handleAuthSectionClick
      }
    ]
  }, [handleAuthSectionClick, handleDesignSectionClick, handleScanDestinationClick, t])
}
